import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";

const TERMS = [10, 15, 20, 25, 35];

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

interface Props {
  /** Valor inicial do imóvel (preço do anúncio). */
  propertyPrice?: number | null;
}

/**
 * Simulador de financiamento imobiliário (PRICE e SAC).
 * Cálculo 100% no cliente — nenhum dado é enviado.
 */
const FinancingSimulator = ({ propertyPrice }: Props) => {
  const initialPrice = propertyPrice && propertyPrice > 0 ? Math.round(propertyPrice) : 0;
  const [price, setPrice] = useState<string>(initialPrice ? String(initialPrice) : "");
  const [down, setDown] = useState<string>(initialPrice ? String(Math.round(initialPrice * 0.2)) : "");
  const [years, setYears] = useState<number>(20);
  const [rate, setRate] = useState<string>("11.5");
  const [system, setSystem] = useState<"PRICE" | "SAC">("PRICE");

  const priceNum = Number(price) || 0;
  const downNum = Number(down) || 0;
  const rateNum = Number(rate.replace(",", ".")) || 0;
  const minDown = priceNum * 0.2;

  const priceError = price.trim() === "" || priceNum <= 0;
  const downError = down.trim() === "";
  const downBelowMin = !downError && priceNum > 0 && downNum < minDown;

  const result = useMemo(() => {
    const financed = priceNum - downNum;
    if (priceNum <= 0 || financed <= 0 || rateNum <= 0) return null;
    const months = years * 12;
    // taxa anual efetiva → mensal equivalente
    const i = Math.pow(1 + rateNum / 100, 1 / 12) - 1;

    if (system === "PRICE") {
      const pmt = (financed * i) / (1 - Math.pow(1 + i, -months));
      return { financed, months, first: pmt, last: pmt, total: pmt * months };
    }
    const amort = financed / months;
    const first = amort + financed * i;
    const last = amort + amort * i;
    const total = months * amort + i * ((financed + amort) * months) / 2;
    return { financed, months, first, last, total };
  }, [priceNum, downNum, rateNum, years, system]);

  return (
    <section className="py-16 bg-muted" id="simulador-financiamento">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto rounded-xl border border-border bg-card p-6 sm:p-8 shadow-lg">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground text-center">
            <Calculator className="inline w-7 h-7 mr-2 text-accent" />
            Simulador de Financiamento
          </h2>
          <p className="font-body text-muted-foreground text-center mt-3">
            Uma forma mais inteligente de comprar seu imóvel. Simule gratuitamente.
          </p>

          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="fin-price">Valor do Imóvel (R$)</Label>
              <Input
                id="fin-price"
                type="number"
                inputMode="numeric"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="1500000"
                className="mt-2"
              />
              {priceError && (
                <p className="mt-1 text-sm text-destructive">Valor do imóvel é obrigatório!</p>
              )}
            </div>

            <div>
              <Label htmlFor="fin-down">Valor de Entrada (R$)</Label>
              <Input
                id="fin-down"
                type="number"
                inputMode="numeric"
                min={0}
                value={down}
                onChange={(e) => setDown(e.target.value)}
                placeholder="300000"
                className="mt-2"
              />
              {downError ? (
                <p className="mt-1 text-sm text-destructive">Valor de entrada é obrigatório!</p>
              ) : downBelowMin ? (
                <p className="mt-1 text-sm text-destructive">
                  O valor mínimo de entrada deve ser 20% do valor do imóvel ({brl(minDown)}).
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Entrada mínima recomendada: 20% do valor do imóvel.
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <Label>Prazo</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TERMS.map((y) => (
                  <Button
                    key={y}
                    type="button"
                    variant={years === y ? "default" : "outline"}
                    onClick={() => setYears(y)}
                    className="min-w-[64px]"
                  >
                    {y}
                  </Button>
                ))}
              </div>
              <p className="mt-2 font-body text-sm text-muted-foreground">
                {years} anos ({years * 12} meses)
              </p>
            </div>

            <div>
              <Label htmlFor="fin-rate">Taxa de Juros Anual (%)</Label>
              <Input
                id="fin-rate"
                type="number"
                step="0.1"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Sistema de Amortização</Label>
              <div className="mt-2 flex gap-2">
                {(["PRICE", "SAC"] as const).map((s) => (
                  <Button
                    key={s}
                    type="button"
                    variant={system === s ? "default" : "outline"}
                    onClick={() => setSystem(s)}
                    className="flex-1"
                  >
                    {s}
                  </Button>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                PRICE: parcelas fixas · SAC: amortização constante (parcelas decrescentes).
              </p>
            </div>
          </div>

          {result && (
            <div className="mt-8 rounded-lg border border-border bg-muted/50 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Valor financiado</p>
                  <p className="font-display text-xl font-bold text-foreground">{brl(result.financed)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {system === "PRICE" ? "Parcela mensal" : "1ª parcela"}
                  </p>
                  <p className="font-display text-xl font-bold text-accent">{brl(result.first)}</p>
                </div>
                {system === "SAC" && (
                  <div>
                    <p className="text-sm text-muted-foreground">Última parcela</p>
                    <p className="font-display text-xl font-bold text-foreground">{brl(result.last)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Total estimado ({result.months} meses)</p>
                  <p className="font-display text-xl font-bold text-foreground">{brl(result.total)}</p>
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Simulação estimada, sem seguros e taxas administrativas. Os valores finais dependem
                da análise de crédito do banco.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FinancingSimulator;
